/**
 * Engine Editor brands.enforces_map column-surgical edit affordance — slide-in
 * panel from right per Sol direction at Day 2.5 P0 close + Day 3 P2 build-prompt.
 *
 * Day 3 P2 scope: wires brands.enforces_map V1 4a per scope §3.7 column-surgical
 * extraction + §5.5.1 ownership-cascading composition. Renders at-cell only
 * (no dedicated route per scope §2 dedicated-route convention — single boolean
 * doesn't warrant route-altitude separation).
 *
 * Gate-refusal-with-pathway (§6.2) is enforced by the parent sub-region BEFORE
 * panel opens — per §6.2: "no edit form opens — the deflection IS the affordance."
 * Panel opens only when:
 *   - Admin wants flip-to-false (never gated; disabling enforcement always allowed)
 *   - Admin wants flip-to-true AND brand has products with map_price populated
 *     (gate check by detectMapEnforcementGate at parent sub-region per P3 flow)
 *
 * Mirrors EngineEditorOwnershipCascadingPanel.tsx slide-in shell structure
 * (fixed top-0 right-0 w-96 h-full, Esc-dismiss per ConfirmDialog.tsx:39-46
 * pattern, backdrop overlay). Specialized content for brands.enforces_map per
 * scope §5.5.1 amended: confirmation prompt + annotation slot + propagation
 * preview + notification + reversibility framing + residue affordance.
 *
 * Slide-in shell layout duplicated from EngineEditorOwnershipCascadingPanel
 * pending Day 4 P2 derived-material consideration on shared shell extraction
 * (rule-of-three at second ownership-cascading consumer per CLAUDE.md
 * Convention 38 catalogue character framework).
 */

import { useEffect, useState, type JSX } from 'react';
import { X } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

export interface BrandRowState {
  id: string;
  name: string;
  enforces_map: boolean;
}

export interface EngineEditorBrandsEnforcesMapPanelProps {
  brand: BrandRowState;
  onSaved: () => void;
  onClose: () => void;
}

export default function EngineEditorBrandsEnforcesMapPanel({
  brand,
  onSaved,
  onClose,
}: EngineEditorBrandsEnforcesMapPanelProps): JSX.Element {
  const [proposedEnforcesMap, setProposedEnforcesMap] = useState<boolean>(brand.enforces_map);
  // DEV-F3 (Fix Pass A) — the L7-trace annotation is V2-scope (ADR-39 / Engine-Editor
  // scope-doc); handleSave never persisted it (no DB column). The slot below is now a
  // disabled coming-soon affordance — NO state, NO persistence.
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Esc-dismiss per ConfirmDialog.tsx:39-46 pattern (mirror Day 2.5 panel).
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && !saving) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [saving, onClose]);

  const direction =
    brand.enforces_map === proposedEnforcesMap
      ? 'no-op'
      : proposedEnforcesMap
        ? 'flip-to-true'
        : 'flip-to-false';

  // Confirmation prompt copy per scope §5.5.1 substrate-shape-honest framing.
  const promptCopy =
    direction === 'flip-to-true'
      ? `Enable MAP enforcement on ${brand.name}. Stance change — engine will flag products with sale prices below brand-published MAP going forward.`
      : direction === 'flip-to-false'
        ? `Disable MAP enforcement on ${brand.name}. Stance change — engine will stop flagging map_price violations on this brand going forward; past below_map flags persist on products from prior enforcement window.`
        : `No change to enforces_map for ${brand.name}.`;

  const handleSave = async (): Promise<void> => {
    if (direction === 'no-op') {
      onClose();
      return;
    }
    setSaving(true);
    setError(null);

    const { error: updateError } = await supabase
      .from('brands')
      .update({ enforces_map: proposedEnforcesMap })
      .eq('id', brand.id);

    if (updateError) {
      setError(updateError.message);
      setSaving(false);
      return;
    }

    setSaving(false);
    onSaved();
  };

  return (
    <>
      {/* Backdrop overlay — mirror Day 2.5 panel */}
      <div
        className="fixed inset-0 bg-black/30 z-[calc(var(--z-modal,200))]"
        onClick={() => !saving && onClose()}
        aria-hidden="true"
      />

      {/* Slide-in panel from right — mirror Day 2.5 panel layout */}
      <aside
        role="dialog"
        aria-label={`Edit MAP enforcement for ${brand.name}`}
        className="fixed top-0 right-0 w-96 h-full z-[calc(var(--z-modal,200)+1)] flex flex-col bg-surface-1 border-l border-border shadow-2xl"
      >
        {/* Header */}
        <header className="flex items-start justify-between gap-2 p-4 border-b border-border">
          <div>
            <h2 className="text-sm font-semibold text-ink-bright">Edit MAP enforcement</h2>
            <p className="text-xs text-ink-muted mt-0.5">{brand.name}</p>
          </div>
          <button
            type="button"
            onClick={() => !saving && onClose()}
            disabled={saving}
            className="text-ink-muted hover:text-ink-primary disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
            aria-label="Close edit panel"
          >
            <X size={16} />
          </button>
        </header>

        {/* Body (scrollable) */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* (1) Confirmation prompt */}
          <div>
            <p className="text-xs text-ink-secondary leading-relaxed">{promptCopy}</p>
          </div>

          {/* New value input — boolean toggle */}
          <div>
            <label
              htmlFor="enforces-map-toggle"
              className="block text-xs font-semibold text-ink-bright mb-1"
            >
              Enforces MAP
            </label>
            <div className="flex items-center gap-2">
              <input
                id="enforces-map-toggle"
                type="checkbox"
                checked={proposedEnforcesMap}
                onChange={(e) => setProposedEnforcesMap(e.target.checked)}
                disabled={saving}
                className="cursor-pointer"
              />
              <span className="text-xs text-ink-secondary">
                {proposedEnforcesMap ? 'true (enforce MAP)' : 'false (do not enforce)'}
              </span>
            </div>
          </div>

          {/* (2) Annotation slot — DEV-F3 coming-soon (V2; not persisted) */}
          <div>
            <label
              htmlFor="enforces-map-annotation"
              className="block text-xs font-semibold text-ink-bright mb-1"
            >
              Rationale (coming soon)
            </label>
            <textarea
              id="enforces-map-annotation"
              disabled
              rows={2}
              placeholder="L7-trace annotation capture is coming in a post-launch release — not saved yet."
              className="w-full px-2 py-1 text-xs bg-surface-2 border border-border rounded text-ink-muted resize-y opacity-60 cursor-not-allowed"
            />
            <p className="mt-1 text-2xs text-ink-muted">Not saved yet — coming post-launch.</p>
          </div>

          {/* (3) Propagation preview — Atlas §6.6.1 L8 inheritance */}
          <div className="bg-surface-2 border border-border-subtle rounded p-3 space-y-1">
            <h3 className="text-xs font-semibold text-ink-bright">Affects on commit</h3>
            <p className="text-xs text-ink-secondary leading-relaxed">
              below_map evaluation on product_profitability cache (engine RPC
              calculate_profitability + siblings): products with sale_price &lt; map_price
              on this brand will be {proposedEnforcesMap ? 'flagged' : 'unflagged'} going
              forward.
            </p>
          </div>

          {/* (4) Notification per CF 68 */}
          <div>
            <p className="text-xs text-ink-muted italic">
              Notifies: downstream consumers (BuyingSession ranking surfaces +
              product_profitability cache) on commit per CF 68.
            </p>
          </div>

          {/* (5) Forward-only reversibility framing per §5.5.1 verbatim */}
          <div>
            <p className="text-xs text-ink-muted italic">
              This edit takes effect going forward. Past below_map flags persist on
              products from prior enforcement window.
            </p>
          </div>

          {/* (6) Residue visibility affordance per Atlas §4.7 inspect-altitude pattern */}
          <div>
            <button
              type="button"
              onClick={() => {
                // V1 scaffolding per Atlas §4.7 — full residue inspection at V2
                // promotion build day. Past below_map flags from prior enforcement
                // window — distinct from bswc's past-calculations residue.
                // eslint-disable-next-line no-console
                console.info(
                  '[engine-editor] enforces_map residue visibility wires at V2 promotion build day',
                );
              }}
              disabled={saving}
              className="text-xs text-accent hover:underline cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
            >
              View past below_map flags from prior enforcement window →
            </button>
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded p-2">
              <p className="text-xs text-red-500">Save failed: {error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <footer className="flex items-center justify-end gap-2 p-4 border-t border-border">
          <button
            type="button"
            onClick={() => !saving && onClose()}
            disabled={saving}
            className="px-3 py-1.5 text-xs text-ink-secondary hover:text-ink-primary disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || direction === 'no-op'}
            className="px-3 py-1.5 text-xs bg-accent text-surface-0 rounded hover:bg-accent/80 disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </footer>
      </aside>
    </>
  );
}
